/**
 * The Changes log — `GET /v1/communities/{id}/admin-actions`.
 *
 * Every mutation a person made to this community's automations, webhooks and boards.
 * READ-ONLY by design: there is no route that appends to it, because a log that can be
 * written to without changing anything is not an audit trail.
 *
 * Not in `@api` for the same reason `automations.ts` and `webhooks.ts` are not — the
 * extension does not speak this surface, it is admin-only.
 */
import { apiGet } from './transport';

/** One recorded change. `before`/`after` carry THE CHANGED FIELDS ONLY, already reduced
 *  to something safe by the server: a webhook URL arrives as `url_host`, an edited DM
 *  step as `{after, chars, sha256, head}`. Both are open shapes — a field added to the
 *  reducers on the server must render here rather than disappear. */
export interface AdminActionItem {
  id: number;
  occurred_at: string | null;
  user_id: number;
  /** Null for an account that has since been deleted: the change is the record, the
   *  person is a lookup, and the lookup is allowed to come back empty.
   *
   *  When `is_staff` is set these are the server's generalisation, not the person —
   *  "Sideguide team" and no address — unless you are a superadmin, who is served the
   *  real identity. Do not try to undo either: the masking is the server's decision and
   *  the client is not told enough to second-guess it. */
  user_name?: string | null;
  user_email?: string | null;
  /** The change was made by Sideguide staff reaching this community by role, rather than
   *  by anyone the owner added. Set whether or not the identity was masked. */
  is_staff?: boolean;
  object_kind: string;
  object_key?: string | null;
  object_id?: number | null;
  verb: string;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  reason?: string | null;
  /** `admin` (a browser session) or `device` (a paired Helper) — which door the change
   *  came through. Null on rows recorded before the gate reported one. */
  auth_kind?: string | null;
}

export interface AdminActionPage {
  items: AdminActionItem[];
  next_cursor: string | null;
  total: number | null;
  /** The object kinds this log can hold. Built from the SERVER's vocabulary so the
   *  filter cannot drift from what is actually recorded. */
  kinds: string[];
}

export function fetchAdminActions(
  skoolId: number,
  opts: { kind?: string | null; cursor?: string | null; limit?: number } = {},
): Promise<AdminActionPage> {
  return apiGet<AdminActionPage>(`/v1/communities/${skoolId}/admin-actions`, {
    kind: opts.kind || undefined,
    cursor: opts.cursor || undefined,
    limit: opts.limit ?? 50,
  });
}

// --- how one row reads --------------------------------------------------------------

/** What kind of thing was changed, in the owner's words. An unknown kind humanises
 *  rather than hiding — the server owns this vocabulary and it can grow. */
const KIND_LABEL: Record<string, string> = {
  automation: 'Auto DM',
  webhook_endpoint: 'Webhook',
  webhook_trigger: 'Webhook trigger',
  pipeline: 'Board',
  crm_card: 'Card',
};

export function kindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? humanizeToken(kind);
}

/** What was done. Present tense, because the timestamp beside it already says when. */
const VERB_LABEL: Record<string, string> = {
  create: 'added',
  update: 'edited',
  delete: 'deleted',
  archive: 'archived',
  enable: 'turned on',
  disable: 'turned off',
  set_sender: 'changed the sender of',
  remove_enrollment: 'removed a member from',
  test: 'tested',
  move: 'moved',
  pin: 'pinned',
  unpin: 'unpinned',
};

export function verbLabel(verb: string): string {
  return VERB_LABEL[verb] ?? humanizeToken(verb);
}

/** The whole line: "turned on Auto DM welcome-dm". The key is the object's own name on
 *  this surface, so it is what the owner recognises. */
export function actionSentence(a: AdminActionItem): string {
  const object = a.object_key ? `${kindLabel(a.object_kind)} ${a.object_key}`
    : kindLabel(a.object_kind);
  return `${verbLabel(a.verb)} ${object}`;
}

function humanizeToken(s: string): string {
  const t = s.replace(/[._]/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

// --- the before/after shapes --------------------------------------------------------

/** One field of a before/after snapshot, rendered. */
export interface FieldLine {
  label: string;
  before: string;
  after: string;
  changed: boolean;
}

/**
 * A field VALUE as a line of text.
 *
 * The server reduces the three values it may not keep before they ever reach here, so
 * this is where those reduced shapes are turned back into something readable:
 *
 *   * `url_host` — a webhook URL with its path dropped, because the path IS the secret
 *     on a Zapier or Make catch hook. Rendered as the host it is, never dressed up as
 *     a URL somebody could try to follow.
 *   * a DM step — `{after, chars, sha256, head}`: the delay, the length, a digest and
 *     the opening. Rendered as the opening plus the length, which is what makes "this
 *     message changed" recognisable; the digest is what PROVES it changed and is not
 *     worth a line of its own.
 *
 * Anything else falls through to a generic rendering, because the reducers grow.
 */
export function fieldValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  if (typeof value === 'number' || typeof value === 'string') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return 'none';
    // ONE PER LINE when the elements are records — a sequence's four DM steps joined by
    // commas is a single unreadable line, and the whole point of keeping a digest and an
    // opening is being able to see WHICH message changed.
    const sep = value.some((v) => v !== null && typeof v === 'object') ? '\n' : ', ';
    return value.map(fieldValue).join(sep);
  }
  const obj = value as Record<string, unknown>;
  if (typeof obj['head'] === 'string' || typeof obj['sha256'] === 'string') {
    const head = typeof obj['head'] === 'string' ? obj['head'] : '';
    const chars = typeof obj['chars'] === 'number' ? obj['chars'] : null;
    const after = typeof obj['after'] === 'string' ? obj['after'] : '';
    const body = head ? `“${head}${chars && chars > head.length ? '…' : ''}”` : 'an empty message';
    return [after ? `after ${after}` : '', body, chars === null ? '' : `${chars} characters`]
      .filter(Boolean).join(' · ');
  }
  return Object.entries(obj).map(([k, v]) => `${fieldLabel(k)} ${fieldValue(v)}`).join(', ');
}

const FIELD_LABEL: Record<string, string> = {
  url_host: 'Address',
  sender_external_id: 'Sends as',
  stop_on: 'Stops on',
  enabled_at: 'On since',
  cursor_seq: 'From event',
  member_external_id: 'Member',
  stopped_reason: 'Stopped because',
  status_code: 'Answered',
  parked_reason: 'Paused because',
  steps: 'Messages',
  stages: 'Columns',
};

export function fieldLabel(key: string): string {
  return FIELD_LABEL[key] ?? humanizeToken(key);
}

/**
 * The two snapshots, zipped into one list of fields.
 *
 * ONE ROW PER FIELD, INCLUDING THE UNCHANGED ONES — a `create` has no `before` at all
 * and an `update` usually changes one field of six, so dropping the rest would leave the
 * reader unable to tell "it was already off" from "we did not record it". `changed`
 * marks the ones that moved so the eye can go straight to them.
 */
export function fieldLines(a: AdminActionItem): FieldLine[] {
  const before = a.before ?? {};
  const after = a.after ?? {};
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort();
  return keys.map((key) => {
    const b = fieldValue(before[key]);
    const f = fieldValue(after[key]);
    return {
      label: fieldLabel(key),
      before: b,
      after: f,
      // A field only present on one side is a change; a create has nothing to compare
      // against, which is why the absence itself is the signal rather than an equality.
      changed: b !== f,
    };
  });
}
